import { defineStore } from "pinia";
import { notificationsApi } from "@/services/notifications";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import type {
  Notification,
  NotificationCategory,
  NotificationSeverity,
} from "@/types";
import * as signalR from "@microsoft/signalr";

export const useNotificationStore = defineStore({
  id: "notifications",
  state: () => ({
    notifications: [] as Notification[],
    unreadCount: 0,
    loading: false,
    error: "" as string,
    filters: {
      showUnreadOnly: false,
      category: null as NotificationCategory | null,
      severity: null as NotificationSeverity | null,
    },
    signalRConnection: null as signalR.HubConnection | null,
    isConnected: false,
  }),
  getters: {
    unreadNotifications: (state) => {
      return state.notifications.filter((n) => !n.isRead);
    },
    notificationsByCategory: (state) => {
      return (category: NotificationCategory) => {
        return state.notifications.filter((n) => n.category === category);
      };
    },
    hasUnread: (state) => {
      return state.unreadCount > 0;
    },
  },
  actions: {
    /**
     * Fetch notifications from the API
     */
    async fetchNotifications(page: number = 1, pageSize: number = 50) {
      this.loading = true;
      this.error = "";

      try {
        const notifications = await notificationsApi.getNotifications({
          isRead: this.filters.showUnreadOnly ? false : undefined,
          category: this.filters.category ?? undefined,
          severity: this.filters.severity ?? undefined,
          page,
          pageSize,
        });

        this.notifications = notifications;
      } catch (error) {
        if (typeof error === "string") {
          this.error = error;
        } else if (error instanceof Error) {
          this.error = error.message;
        }
      } finally {
        this.loading = false;
      }
    },

    /**
     * Load unread notification count
     */
    async loadUnreadCount() {
      try {
        this.unreadCount = await notificationsApi.getUnreadCount();
      } catch (error) {
        console.error("Failed to load unread count:", error);
      }
    },

    /**
     * Mark a notification as read
     */
    async markAsRead(notificationId: string) {
      try {
        await notificationsApi.markAsRead(notificationId);

        // Update local state
        const notification = this.notifications.find(
          (n) => n.id === notificationId
        );
        if (notification && !notification.isRead) {
          notification.isRead = true;
          notification.readAt = new Date().toISOString();
          this.unreadCount = Math.max(0, this.unreadCount - 1);
        }
      } catch (error) {
        if (typeof error === "string") {
          this.error = error;
        } else if (error instanceof Error) {
          this.error = error.message;
        }
      }
    },

    /**
     * Mark all notifications as read
     */
    async markAllAsRead() {
      try {
        await notificationsApi.markAllAsRead();

        // Update local state
        this.notifications.forEach((n) => {
          if (!n.isRead) {
            n.isRead = true;
            n.readAt = new Date().toISOString();
          }
        });
        this.unreadCount = 0;
      } catch (error) {
        if (typeof error === "string") {
          this.error = error;
        } else if (error instanceof Error) {
          this.error = error.message;
        }
      }
    },

    /**
     * Delete a notification
     */
    async deleteNotification(notificationId: string) {
      try {
        await notificationsApi.deleteNotification(notificationId);

        // Remove from local state
        const index = this.notifications.findIndex(
          (n) => n.id === notificationId
        );
        if (index !== -1) {
          const wasUnread = !this.notifications[index].isRead;
          this.notifications.splice(index, 1);
          if (wasUnread) {
            this.unreadCount = Math.max(0, this.unreadCount - 1);
          }
        }
      } catch (error) {
        if (typeof error === "string") {
          this.error = error;
        } else if (error instanceof Error) {
          this.error = error.message;
        }
      }
    },

    /**
     * Add a notification (typically from SignalR)
     */
    addNotification(notification: Notification) {
      // Add to the beginning of the array
      this.notifications.unshift(notification);
      if (!notification.isRead) {
        this.unreadCount++;
      }
    },

    /**
     * Set filter for showing only unread notifications
     */
    setShowUnreadOnly(value: boolean) {
      this.filters.showUnreadOnly = value;
    },

    /**
     * Set category filter
     */
    setCategoryFilter(category: NotificationCategory | null) {
      this.filters.category = category;
    },

    /**
     * Set severity filter
     */
    setSeverityFilter(severity: NotificationSeverity | null) {
      this.filters.severity = severity;
    },

    /**
     * Clear all filters
     */
    clearFilters() {
      this.filters = {
        showUnreadOnly: false,
        category: null,
        severity: null,
      };
    },

    /**
     * Initialize SignalR connection for real-time notifications
     */
    async initializeSignalR() {
      if (this.signalRConnection) {
        console.log("SignalR already initialized");
        return;
      }

      try {
        // Azure SignalR runs in Serverless mode: no host holds a hub, so we ask the API for a ticket and
        // connect to the service with it. The ticket names the authenticated user, which is the address
        // every notification is sent to. Asked for again on reconnect so an expired token is replaced.
        const ticket = () =>
          fetchWrapper.post<{ url: string; accessToken: string }>(
            `${import.meta.env.VITE_API_URL}/realtime/notifications/negotiate`
          );

        const first = await ticket();
        const connection = new signalR.HubConnectionBuilder()
          .withUrl(first.url, { accessTokenFactory: async () => (await ticket()).accessToken })
          .withAutomaticReconnect()
          .configureLogging(signalR.LogLevel.Information)
          .build();

        // Handle incoming notifications
        connection.on("ReceiveNotification", (notification: Notification) => {
          console.log("Received notification via SignalR:", notification);
          this.addNotification(notification);
        });

        // Handle connection state changes
        connection.onreconnecting(() => {
          console.log("SignalR reconnecting...");
          this.isConnected = false;
        });

        connection.onreconnected(() => {
          console.log("SignalR reconnected");
          this.isConnected = true;
        });

        connection.onclose(() => {
          console.log("SignalR connection closed");
          this.isConnected = false;
        });

        // Start the connection
        await connection.start();
        console.log("SignalR connected");
        this.signalRConnection = connection;
        this.isConnected = true;
      } catch (error) {
        console.error("Error initializing SignalR:", error);
        this.error =
          error instanceof Error ? error.message : "SignalR connection failed";
      }
    },

    /**
     * Stop SignalR connection
     */
    async stopSignalR() {
      if (this.signalRConnection) {
        try {
          await this.signalRConnection.stop();
          console.log("SignalR disconnected");
        } catch (error) {
          console.error("Error stopping SignalR:", error);
        } finally {
          this.signalRConnection = null;
          this.isConnected = false;
        }
      }
    },
  },
});
